// Quem está de plantão — uma pessoa de cada vez.
//
// "De plantão", não "no quarto": só a paciente fica no quarto. A equipe atende
// e segue andando pelo hospital; a família é que fica ao lado da cama. O termo
// precisa valer para os dois.
//
// Precisa ser de poucos toques, em pé no corredor, com o celular numa mão só.
// Por isso os nomes são texto livre, sem cadastro nenhum atrás, e a função é
// escolhida em botões grandes em vez de uma lista suspensa.
//
// Redesenhada em 19/09 a partir de um desenho do dono, depois de uma primeira
// tentativa que ele chamou de confusa — com razão: eu tinha conferido a tela
// VAZIA, e a poluição estava justamente na parte com dados. Daí a separação
// abaixo entre `Plantao` (busca e estado) e `Corpo` (só desenho): a ferramenta
// `conferir-plantao.mts` renderiza o `Corpo` com dados de exemplo, e dá para
// olhar a tela cheia sem logar.
//
// Quem decide se um registro abre turno novo ou entra no que está aberto é o
// servidor (ver `rotas/plantao.ts`), não esta tela.
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  PAPEIS, comTratamento, formatarDiaMes, formatarHora, diaDoInstante, hojeISO,
  agoraParaCampo, doCampoParaISO,
} from '@medicov1/shared';
import { chamar } from '../api';
import { Ditar } from '../componentes/Ditar';
import './plantao.css';

export interface ProfissionalDTO {
  id?: string;
  papel: string;
  nome: string;
  /** Desde quando esta pessoa está de plantão. */
  desdeEm?: string;
  /** Até quando ficou. Nulo = ainda está. */
  ateEm?: string | null;
}

export interface PlantaoDTO {
  id: string;
  turno: 'manha' | 'tarde' | 'noite';
  observacao: string | null;
  inicioEm: string;
  fimEm: string | null;
  emCurso: boolean;
  profissionais: ProfissionalDTO[];
}

const ROTULO_TURNO: Record<PlantaoDTO['turno'], string> = {
  manha: 'Manhã', tarde: 'Tarde', noite: 'Noite',
};

/**
 * As funções que aparecem como botão, na ordem em que se usa.
 *
 * Família primeiro porque é o registro que faz o relatório de passar o turno
 * existir. O rótulo é curto de propósito: quatro botões precisam caber numa
 * linha de celular, e "Enfermeiro(a)" em duas linhas dobra a altura da fileira.
 * O nome completo aparece na lista de quem está de plantão.
 */
const ATALHOS: { chave: string; curto: string; curtoNaLista: string }[] = [
  { chave: 'familiar', curto: 'Familiar', curtoNaLista: 'Familiar' },
  { chave: 'enfermeiro', curto: 'Enferm.', curtoNaLista: 'Enfermagem' },
  { chave: 'tecnico_enfermagem', curto: 'Técnico', curtoNaLista: 'Téc. de enfermagem' },
  { chave: 'medico', curto: 'Médico', curtoNaLista: 'Medicina' },
];
const EH_ATALHO = new Set(ATALHOS.map((a) => a.chave));

export const equipeEmTexto = (p: { profissionais: ProfissionalDTO[] }) =>
  p.profissionais.map((x) => comTratamento(x.papel, x.nome)).join(' · ');

const rotuloDoPapel = (chave: string) =>
  PAPEIS.find((p) => p.chave === chave)?.rotulo ?? chave;

/**
 * "18/09 às 20:53" — o formato que a lista usa para dizer desde quando.
 *
 * O `new Date()` no meio não é enfeite: no shared, string é DIA PURO e não se
 * converte, `Date` é INSTANTE e vai para o fuso de Brasília. Passando a string
 * direto, quem entrou às 20:53 de ontem aparecia como tendo entrado hoje —
 * porque em UTC o instante já é do dia seguinte.
 */
const desdeEmTexto = (iso: string) => {
  // Hoje dispensa a data: "desde 08:54" em vez de "desde 19/09 às 08:54". A
  // linha inteira — função, ponto, desde, data, hora — não cabe em 375 px e
  // quebrava em duas, empurrando a pessoa seguinte para baixo. E a data de hoje
  // não informa nada a quem está lendo hoje.
  const dia = diaDoInstante(new Date(iso));
  if (dia === hojeISO()) return formatarHora(iso);
  return `${formatarDiaMes(new Date(iso))} às ${formatarHora(iso)}`;
};

/**
 * O nome da função na lista, curto.
 *
 * "Familiar / acompanhante · desde 18/09 às 20:53" não cabe numa linha de
 * celular, e a segunda linha empurra a pessoa seguinte para baixo.
 */
const rotuloCurto = (chave: string) =>
  ATALHOS.find((a) => a.chave === chave)?.curtoNaLista ?? rotuloDoPapel(chave);

/**
 * As iniciais do círculo: uma ou duas letras, nunca mais.
 *
 * "Ilton" vira I, "Fran Souza" vira FS. É o que permite reconhecer a pessoa na
 * lista antes de ler o nome, e é o mesmo truque de toda agenda de telefone.
 */
function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0]!.slice(0, 1).toUpperCase();
  return (partes[0]!.slice(0, 1) + partes[partes.length - 1]!.slice(0, 1)).toUpperCase();
}

/**
 * A cor do círculo sai do NOME, não do papel.
 *
 * Duas enfermeiras diferentes precisam de círculos diferentes — é o que separa
 * uma da outra de relance. A mesma pessoa mantém a cor entre um dia e outro,
 * porque a conta é sempre a mesma sobre as mesmas letras.
 */
function corDoNome(nome: string): string {
  let soma = 0;
  for (const letra of nome.trim().toLowerCase()) soma = (soma * 31 + letra.charCodeAt(0)) % 360;
  return `hsl(${soma} 58% 52%)`;
}

interface Props {
  pacienteId: string;
  paciente?: { apelido: string; quarto?: string | null } | null;
  aoRegistrar: () => void;
  aoVoltar: () => void;
}

export function Plantao({ pacienteId, paciente, aoRegistrar, aoVoltar }: Props) {
  const [historico, setHistorico] = useState<PlantaoDTO[]>([]);
  const [papel, setPapel] = useState<string>('familiar');
  const [nome, setNome] = useState('');
  /**
   * Data e hora em campos separados.
   *
   * Um `datetime-local` só é um campo com duas metades que o teclado do celular
   * trata de jeitos diferentes, e no iPhone abre uma roleta em que se mexe nos
   * dois ao mesmo tempo. Separados, cada um tem o seu teclado e o seu rótulo.
   */
  const [data, setData] = useState(() => agoraParaCampo().slice(0, 10));
  const [hora, setHora] = useState(() => agoraParaCampo().slice(11, 16));
  const [observacao, setObservacao] = useState('');
  /**
   * O que fazer com quem já está na mesma função: render ou entrar junto.
   *
   * Null enquanto ninguém respondeu — e a tela não deixa gravar sem resposta
   * quando a pergunta cabe, porque adivinhar aqui apaga o plantão de alguém.
   */
  const [modo, setModo] = useState<'rendeu' | 'junto' | null>(null);
  /** Encerrar o turno em curso numa hora que já passou. */
  const [fimData, setFimData] = useState(() => agoraParaCampo().slice(0, 10));
  const [fimHora, setFimHora] = useState(() => agoraParaCampo().slice(11, 16));
  const [encerrando, setEncerrando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [salvo, setSalvo] = useState('');

  const carregar = useCallback(async () => {
    try {
      setHistorico(await chamar<PlantaoDTO[]>(`/plantoes?pacienteId=${pacienteId}`));
      setErro('');
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui carregar o histórico');
    }
  }, [pacienteId]);

  useEffect(() => { void carregar(); }, [carregar]);

  const atual = historico.find((p) => p.emCurso) ?? null;

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!nome.trim()) {
      setErro('Falta o nome — sem ele o registro não responde nada depois.');
      return;
    }

    setEnviando(true);
    setErro('');
    try {
      const r = await chamar<{ trocouOTurno: boolean; renderam: string[] }>('/plantoes/pessoa', {
        metodo: 'POST',
        corpo: {
          pacienteId, papel, nome: nome.trim(),
          desdeEm: doCampoParaISO(`${data}T${hora}`),
          ...(modo ? { modo } : {}),
          ...(observacao.trim() ? { observacao: observacao.trim() } : {}),
        },
      });
      // `?? []` não é paranoia: esta tela já quebrou na mão do dono porque um
      // campo que o servidor mandava não chegava até aqui. Uma confirmação que
      // some é irritante; uma tela que estoura depois de gravar faz a pessoa
      // achar que não gravou — e registrar tudo de novo.
      const renderam = r?.renderam ?? [];
      setSalvo(r?.trocouOTurno
        ? `${nome.trim()} assumiu o plantão — o anterior foi encerrado nesta hora.`
        : renderam.length > 0
          ? `${nome.trim()} entrou e rendeu ${renderam.join(' e ')}.`
          : `${nome.trim()} entrou no plantão.`);
      setModo(null);
      setNome('');
      setObservacao('');
      setModo(null);
      setData(agoraParaCampo().slice(0, 10));
      setHora(agoraParaCampo().slice(11, 16));
      await carregar();
      aoRegistrar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui gravar.');
    } finally {
      setEnviando(false);
    }
  }

  /** Fecha o plantão em curso na hora informada, sem abrir outro. */
  async function encerrar() {
    if (!atual) return;
    setEncerrando(true);
    setErro('');
    try {
      await chamar(`/plantoes/${atual.id}`, {
        metodo: 'PATCH',
        corpo: { fimEm: doCampoParaISO(`${fimData}T${fimHora}`) },
      });
      setSalvo('Plantão encerrado.');
      await carregar();
      aoRegistrar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui encerrar o plantão');
    } finally {
      setEncerrando(false);
    }
  }

  /**
   * Registra que a pessoa saiu — sem apagar que ela esteve.
   *
   * É o que se usa quando o enfermeiro troca: o anterior sai às 14h e continua
   * no histórico respondendo por quem estava de manhã.
   */
  async function registrarSaida(id: string, quando: string) {
    setErro('');
    try {
      await chamar(`/plantoes/pessoa/${id}`, {
        metodo: 'PATCH',
        corpo: { ateEm: quando },
      });
      setSalvo('Saída registrada.');
      await carregar();
      aoRegistrar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui registrar a saída');
    }
  }

  /** Tira alguém do plantão: foi posto por engano, ou o nome saiu errado. */
  async function tirar(id: string) {
    setErro('');
    try {
      await chamar(`/plantoes/pessoa/${id}`, { metodo: 'DELETE' });
      await carregar();
      aoRegistrar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui tirar do plantão');
    }
  }

  return (
    <Corpo
      paciente={paciente ?? null}
      atual={atual}
      historico={historico}
      papel={papel} aoTrocarPapel={setPapel}
      nome={nome} aoTrocarNome={setNome}
      data={data} aoTrocarData={setData}
      hora={hora} aoTrocarHora={setHora}
      observacao={observacao} aoTrocarObservacao={setObservacao}
      modo={modo} aoTrocarModo={setModo}
      fimData={fimData} aoTrocarFimData={setFimData}
      fimHora={fimHora} aoTrocarFimHora={setFimHora}
      erro={erro} salvo={salvo} enviando={enviando} encerrando={encerrando}
      aoEnviar={enviar} aoEncerrar={() => void encerrar()} aoTirar={(id) => void tirar(id)}
      aoRegistrarSaida={(id, q) => void registrarSaida(id, q)}
      aoVoltar={aoVoltar}
    />
  );
}

interface CorpoProps {
  paciente: { apelido: string; quarto?: string | null } | null;
  atual: PlantaoDTO | null;
  historico: PlantaoDTO[];
  papel: string; aoTrocarPapel: (v: string) => void;
  nome: string; aoTrocarNome: (v: string) => void;
  data: string; aoTrocarData: (v: string) => void;
  hora: string; aoTrocarHora: (v: string) => void;
  observacao: string; aoTrocarObservacao: (v: string) => void;
  modo: 'rendeu' | 'junto' | null; aoTrocarModo: (v: 'rendeu' | 'junto' | null) => void;
  fimData: string; aoTrocarFimData: (v: string) => void;
  fimHora: string; aoTrocarFimHora: (v: string) => void;
  erro: string; salvo: string; enviando: boolean; encerrando: boolean;
  aoEnviar: (e: FormEvent) => void;
  aoEncerrar: () => void;
  aoTirar: (id: string) => void;
  aoRegistrarSaida: (id: string, quando: string) => void;
  aoVoltar: () => void;
}

/** Só o desenho. Sem busca, sem estado — é o que a ferramenta de prova monta. */
export function Corpo({
  paciente, atual, historico,
  papel, aoTrocarPapel, nome, aoTrocarNome, data, aoTrocarData, hora, aoTrocarHora,
  observacao, aoTrocarObservacao, modo, aoTrocarModo,
  fimData, aoTrocarFimData, fimHora, aoTrocarFimHora,
  erro, salvo, enviando, encerrando, aoEnviar, aoEncerrar, aoTirar, aoRegistrarSaida, aoVoltar,
}: CorpoProps) {
  // Qual pessoa teve o menu "⋮" aberto. Null = nenhum, lista limpa.
  const [menuDe, setMenuDe] = useState<string | null>(null);
  const anteriores = historico.filter((p) => !p.emCurso);

  // Quem está de plantão AGORA: quem entrou e ainda não saiu. Quem saiu some
  // desta lista, mas continua no plantão — só que como história.
  const presentes = (atual?.profissionais ?? []).filter((x) => !x.ateEm);
  const sairam = (atual?.profissionais ?? []).filter((x) => x.ateEm);

  /**
   * Já tem alguém nesta função? Então é preciso perguntar o que aconteceu.
   *
   * "Em geral só tem um enfermeiro de plantão" — mas dois acontece, e os dois
   * filhos no quarto ao mesmo tempo também. Adivinhar aqui apagaria o plantão
   * de alguém, então quem registra responde.
   */
  const ocupando = presentes.filter(
    (x) => x.papel === papel && x.nome.trim().toLowerCase() !== nome.trim().toLowerCase(),
  );
  const precisaEscolher = ocupando.length > 0 && nome.trim().length > 0;

  return (
    <main className="pl">
      <header className="pl__topo">
        <button className="pl__voltar" onClick={aoVoltar} aria-label="Voltar ao monitor" />
        <div>
          <h1>Plantão</h1>
          {paciente && (
            <p>{paciente.apelido}{paciente.quarto ? ` · Quarto ${paciente.quarto}` : ''}</p>
          )}
        </div>
      </header>

      {/* Quem está agora, em uma frase. A lista de nomes vem depois do
          formulário, porque quem abre esta tela vem para REGISTRAR. */}
      {atual ? (
        <section className="pl__atual">
          <span className="pl__atual__selo"><i />Plantão atual</span>
          <b>{ROTULO_TURNO[atual.turno]} · em andamento</b>
          <small>Desde {desdeEmTexto(atual.inicioEm)}</small>
        </section>
      ) : (
        <section className="pl__atual pl__atual--vazio">
          <span className="pl__atual__selo"><i />Sem plantão aberto</span>
          <small>A primeira pessoa registrada abre o plantão.</small>
        </section>
      )}

      {/* ------------------------------------------------ adicionar pessoa */}
      <form className="pl__cartao" onSubmit={aoEnviar}>
        <h2>Adicionar pessoa</h2>
        <p className="pl__sub">Uma pessoa por vez.</p>

        <div className="pl__campo">
          <label>Função</label>
          <div className="pl__papeis" role="group" aria-label="Função">
            {ATALHOS.map((a) => (
              <button
                type="button"
                key={a.chave}
                className={`pl__papel${papel === a.chave ? ' pl__papel--on' : ''}`}
                onClick={() => aoTrocarPapel(a.chave)}
              >
                {a.curto}
              </button>
            ))}
          </div>
          {/* O catálogo inteiro continua alcançável — fisioterapeuta, fono,
              nutricionista. Quando uma delas é escolhida, o campo fica aceso,
              porque senão não há nada na tela dizendo qual função está valendo. */}
          <select
            className={`pl__outra${EH_ATALHO.has(papel) ? '' : ' pl__outra--on'}`}
            value={EH_ATALHO.has(papel) ? '' : papel}
            onChange={(e) => e.target.value && aoTrocarPapel(e.target.value)}
            aria-label="Outra função"
          >
            <option value="">Outra função…</option>
            {PAPEIS.filter((p) => !EH_ATALHO.has(p.chave)).map((p) => (
              <option key={p.chave} value={p.chave}>{p.rotulo}</option>
            ))}
          </select>
        </div>

        <div className="pl__campo">
          <label htmlFor="nome">Nome</label>
          <div className="comDitado">
            <input id="nome" autoComplete="off" value={nome}
              onChange={(e) => aoTrocarNome(e.target.value)} />
            <Ditar valor={nome} aoMudar={aoTrocarNome} rotulo="ditar o nome" />
          </div>
        </div>

        {/* Data e hora separadas: cada uma com o seu teclado e o seu rótulo. */}
        <div className="pl__duplo">
          <div className="pl__campo">
            <label htmlFor="data">Data</label>
            <input id="data" type="date" value={data}
              onChange={(e) => aoTrocarData(e.target.value)} required />
          </div>
          <div className="pl__campo">
            <label htmlFor="hora">Hora</label>
            <input id="hora" type="time" value={hora}
              onChange={(e) => aoTrocarHora(e.target.value)} required />
          </div>
        </div>

        <div className="pl__campo">
          <label htmlFor="obs">Observação <span>opcional</span></label>
          <div className="comDitado">
            <input id="obs" autoComplete="off" placeholder="plantonista trocou às 15h"
              value={observacao} onChange={(e) => aoTrocarObservacao(e.target.value)} />
            <Ditar valor={observacao} aoMudar={aoTrocarObservacao} rotulo="ditar a observação" />
          </div>
        </div>

        {precisaEscolher && (
          <div className="pl__escolha">
            <p>
              <b>{ocupando.map((x) => comTratamento(x.papel, x.nome)).join(' e ')}</b>
              {ocupando.length === 1 ? ' já está' : ' já estão'} de plantão
              {' '}{ocupando.length === 1 ? 'nesta função' : 'nesta função'}. O que aconteceu?
            </p>
            <div className="pl__escolha__botoes">
              <button type="button"
                className={`pl__escolha__botao${modo === 'rendeu' ? ' pl__escolha__botao--on' : ''}`}
                onClick={() => aoTrocarModo('rendeu')}>
                Rendeu
                <small>{ocupando.length === 1 ? 'sai agora' : 'saem agora'}</small>
              </button>
              <button type="button"
                className={`pl__escolha__botao${modo === 'junto' ? ' pl__escolha__botao--on' : ''}`}
                onClick={() => aoTrocarModo('junto')}>
                Entrou junto
                <small>ficam os dois</small>
              </button>
            </div>
          </div>
        )}

        {erro && <p className="aviso-erro pl__erro">{erro}</p>}
        {salvo && !erro && <p className="pl__salvo">{salvo}</p>}

        <button className="pl__enviar" type="submit"
          disabled={enviando || (precisaEscolher && !modo)}>
          {enviando ? 'Gravando…'
            : precisaEscolher && !modo ? 'Responda acima para continuar'
              : 'Adicionar ao plantão'}
        </button>
        <p className="pl__dica">
          Um <b>familiar</b> que rende outro encerra o plantão de quem estava e
          abre o seguinte — é o que o relatório de passar o turno usa.
        </p>
      </form>

      {/* ------------------------------------------------ quem está de plantão */}
      {atual && presentes.length > 0 && (
        <section className="pl__cartao">
          {/* "No quarto" era impreciso e o dono corrigiu: só a paciente fica no
              quarto. A equipe atende e segue andando pelo hospital — a família
              é que fica. "De plantão" cobre os dois sem mentir sobre nenhum. */}
          <h2>Quem está de plantão <b className="pl__conta">{presentes.length}</b></h2>

          <ul className="pl__pessoas">
            {presentes.map((x) => {
              const id = x.id ?? `${x.papel}-${x.nome}`;
              return (
                <li key={id}>
                  <span className="pl__inicial" style={{ background: corDoNome(x.nome) }}>
                    {iniciais(x.nome)}
                  </span>
                  <span className="pl__pessoa">
                    <b>{comTratamento(x.papel, x.nome)}</b>
                    <small>
                      {rotuloCurto(x.papel)} · desde {desdeEmTexto(x.desdeEm ?? atual.inicioEm)}
                    </small>
                  </span>
                  {x.id && (
                    <button type="button" className="pl__menu"
                      aria-label={`Opções de ${x.nome}`}
                      onClick={() => setMenuDe(menuDe === id ? null : id)}>⋮</button>
                  )}
                  {menuDe === id && x.id && (
                    <div className="pl__menuAberto">
                      {/* Sair e ser apagado são coisas diferentes, e a diferença
                          é o histórico: quem saiu às 14h esteve ali de manhã. */}
                      <button type="button" className="pl__menuAberto__principal"
                        onClick={() => { setMenuDe(null); aoRegistrarSaida(x.id!, new Date().toISOString()); }}>
                        Saiu do plantão agora
                      </button>
                      <button type="button"
                        onClick={() => { setMenuDe(null); aoTirar(x.id!); }}>
                        Tirar — foi engano
                      </button>
                      <button type="button" onClick={() => setMenuDe(null)}>Cancelar</button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Quem já saiu não some: continua respondendo pelas horas em que
              esteve, e é assim que "quem estava às 10h" tem resposta. */}
          {sairam.length > 0 && (
            <p className="pl__sairam">
              Já saíram neste plantão:{' '}
              {sairam.map((x) => `${comTratamento(x.papel, x.nome)} (até ${desdeEmTexto(x.ateEm!)})`).join(' · ')}
            </p>
          )}

          {atual.observacao && <p className="pl__obs">{atual.observacao}</p>}

          {/* Encerrar o plantão INTEIRO — não é o caminho para trocar uma
              pessoa, que se faz no "⋮" dela. É para quando a família sai e
              ninguém assume: a partir dali não há plantão nenhum, e o relatório
              de passar o turno precisa saber onde a janela fecha. */}
          <details className="pl__encerrar">
            <summary>Encerrar o plantão inteiro</summary>
            <p className="pl__encerrar__nota">
              Use quando <b>ninguém assume</b> depois. Para trocar só uma pessoa,
              toque no <b>⋮</b> ao lado do nome dela.
            </p>
            <div className="pl__duplo">
              <div className="pl__campo">
                <label htmlFor="fimData">Data</label>
                <input id="fimData" type="date" value={fimData}
                  onChange={(e) => aoTrocarFimData(e.target.value)} />
              </div>
              <div className="pl__campo">
                <label htmlFor="fimHora">Hora</label>
                <input id="fimHora" type="time" value={fimHora}
                  onChange={(e) => aoTrocarFimHora(e.target.value)} />
              </div>
            </div>
            <button type="button" className="pl__encerrarBotao"
              disabled={encerrando} onClick={aoEncerrar}>
              {encerrando ? 'Encerrando…' : 'Encerrar plantão'}
            </button>
          </details>
        </section>
      )}

      {/* ------------------------------------------------------- histórico */}
      {anteriores.length > 0 && (
        <details className="pl__historico">
          <summary>Plantões anteriores <b className="pl__conta">{anteriores.length}</b></summary>
          <ul>
            {anteriores.map((p) => (
              <li key={p.id}>
                <div className="pl__historico__quando">
                  <b>{ROTULO_TURNO[p.turno]}</b>
                  <small>{desdeEmTexto(p.inicioEm)} → {p.fimEm ? desdeEmTexto(p.fimEm) : 'agora'}</small>
                </div>
                <p>{equipeEmTexto(p) || 'sem nomes registrados'}</p>
                {p.observacao && <em>{p.observacao}</em>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </main>
  );
}
